import type { ReactElement } from "react";

import * as Sentry from "@sentry/react";

import type { LoaderFunction, ShouldRevalidateFunction } from "react-router";

/**
 * The props a preloading route contributes to its `<Route>` in Routes.tsx.
 *
 * Each such route lives in a `*Route.tsx` module colocated with its view,
 * which owns the lazy element, the loader and the revalidation policy;
 * Routes.tsx spreads the module onto the `<Route>` and adds only what the
 * route tree knows — the path.
 */
export interface PreloadingRouteProps {
  /** The page, imported lazily so it stays out of the app shell's bundle. */
  element: ReactElement;
  /** Starts the page's queries as the route is navigated to. */
  loader: LoaderFunction;
  /** When the loader runs again for a navigation within the route. */
  shouldRevalidate: ShouldRevalidateFunction;
}

interface LazyRouteLoaderOptions {
  /** Imports the loader-chunk module and returns the loader to run. */
  load: () => Promise<LoaderFunction>;
  /** Imports the page's chunk, which is downloaded alongside the loader's. */
  warm: () => Promise<unknown>;
}

/**
 * Builds a route loader that starts a page's queries from a chunk of its own.
 *
 * The loader module lives in a chunk far smaller than the page's, so the
 * queries go out while the page is still downloading instead of after it — a
 * loader exported from the page's own module would be back to waiting for it.
 * Warming the page's chunk here rather than leaving it to the lazy element
 * starts both downloads together; the lazy element reuses the import, and
 * reports a failure of it through the route's error boundary.
 *
 * A loader that throws takes the whole route to the router's default error
 * screen — no errorElement is declared, and RootLayout's boundary only sees
 * render errors — so a failed preload resolves to null instead: the pages
 * treat null loader data as "nothing preloaded" and fetch on mount. The
 * likeliest failure is the loader's chunk going stale across a deploy, and
 * then the page's own import fails too and does reach the error boundary.
 */
export function lazyRouteLoader({
  load,
  warm,
}: LazyRouteLoaderOptions): LoaderFunction {
  return (args) => {
    void warm().catch(() => {
      // The lazy element reports the failure when it renders.
    });

    return load()
      .then((loader) => loader(args))
      .catch((error: unknown) => {
        Sentry.captureException(error);
        return null;
      });
  };
}
